import { exhaustiveSwitchError } from './exhaustiveSwitchError'
import { indexBy } from './functions'
import { Integration } from './integration'
import { isNotNullOrUndefined } from './isNotNullOrUndefined'
import { getStaleUnitRentAdjustmentUpdateError } from './staleUnitRentAdjustment'

export function filterPropertiesByExclusionColumns<
  Property extends {
    disabled: boolean
    hidden: boolean
  },
>(
  properties: Property[],
  {
    includeDisabledProperties,
    includeHiddenProperties,
  }: {
    includeDisabledProperties: boolean
    includeHiddenProperties: boolean
  },
  ...additionalPredicates: ((property: Property) => boolean)[]
): Property[] {
  return properties.filter(property => {
    if (!includeDisabledProperties && property.disabled) {
      return false
    }
    if (!includeHiddenProperties && property.hidden) {
      return false
    }
    for (const predicate of additionalPredicates) {
      if (!predicate(property)) {
        return false
      }
    }
    return true
  })
}

/**
 * Resolves the value a nullable property field will hold after an update, following the convention
 * that `undefined` leaves the field unchanged while an explicit `null` clears it.
 */
function resolveNullableUpdate(
  updatedValue: number | null | undefined,
  currentValue: number | null | undefined
): number | null {
  return updatedValue === null ? null : (updatedValue ?? currentValue ?? null)
}

export function validatePropertyFieldsForUpdate({
  updates,
  properties,
}: {
  updates: {
    propertySid: string
    maxAnnualRentChangeTimes?: number | null
    minLeaseDuration?: number | null
    maxLeaseDuration?: number | null
    minLeaseDurationForRenewals?: number | null
    maxLeaseDurationForRenewals?: number | null
    renewalRateTargetLowerBound?: number | null
    renewalRateTargetUpperBound?: number | null
    maxBestTerm?: number | null
    minBestTermForNewLeases?: number | null
    minBestTermForRenewals?: number | null
    staleUnitRentAdjustmentType?: string | null
    staleUnitRentAdjustmentAmount?: number | null
    staleUnitRentAdjustmentDaysSinceAvailableThreshold?: number | null
  }[]
  properties: {
    sid: string
    minLeaseDuration: number
    maxLeaseDuration: number
    minLeaseDurationForRenewals?: number | null
    maxLeaseDurationForRenewals?: number | null
    renewalRateTargetLowerBound: number
    renewalRateTargetUpperBound: number
    maxBestTerm?: number | null
    minBestTermForNewLeases?: number | null
    minBestTermForRenewals?: number | null
    staleUnitRentAdjustmentType?: string | null
    staleUnitRentAdjustmentAmount?: number | null
    staleUnitRentAdjustmentDaysSinceAvailableThreshold?: number | null
  }[]
}):
  | {
      isValid: true
    }
  | {
      isValid: false
      propertySid: string
      humanReadableError: string
    } {
  const propertyBySid = indexBy(properties, property => property.sid)
  for (const update of updates) {
    const property = propertyBySid[update.propertySid]
    if (property === undefined) {
      return {
        isValid: false,
        propertySid: update.propertySid,
        humanReadableError: 'Property not found',
      }
    }

    if (
      isNotNullOrUndefined(update.maxAnnualRentChangeTimes) &&
      update.maxAnnualRentChangeTimes < 1
    ) {
      return {
        isValid: false,
        propertySid: update.propertySid,
        humanReadableError: 'maxAnnualRentChangeTimes must be at least 1',
      }
    }

    const updatedMinLeaseDuration = update.minLeaseDuration ?? property.minLeaseDuration
    const updatedMaxLeaseDuration = update.maxLeaseDuration ?? property.maxLeaseDuration
    // null clears a renewal override; undefined leaves the current value unchanged
    const updatedMinLeaseDurationForRenewals =
      update.minLeaseDurationForRenewals === null
        ? null
        : (update.minLeaseDurationForRenewals ?? property.minLeaseDurationForRenewals ?? null)
    const updatedMaxLeaseDurationForRenewals =
      update.maxLeaseDurationForRenewals === null
        ? null
        : (update.maxLeaseDurationForRenewals ?? property.maxLeaseDurationForRenewals ?? null)

    if (
      isNotNullOrUndefined(update.minLeaseDuration) ||
      isNotNullOrUndefined(update.maxLeaseDuration)
    ) {
      const isMinLeaseDurationGreaterThanMax = updatedMinLeaseDuration > updatedMaxLeaseDuration
      if (isMinLeaseDurationGreaterThanMax) {
        return {
          isValid: false,
          propertySid: update.propertySid,
          humanReadableError: `Min lease duration (${updatedMinLeaseDuration}) cannot be greater than max lease duration (${updatedMaxLeaseDuration})`,
        }
      }
    }

    const isAnyLeaseDurationFieldInUpdate =
      update.minLeaseDuration !== undefined ||
      update.maxLeaseDuration !== undefined ||
      update.minLeaseDurationForRenewals !== undefined ||
      update.maxLeaseDurationForRenewals !== undefined
    if (isAnyLeaseDurationFieldInUpdate) {
      if (
        isNotNullOrUndefined(updatedMinLeaseDurationForRenewals) &&
        isNotNullOrUndefined(updatedMaxLeaseDurationForRenewals) &&
        updatedMinLeaseDurationForRenewals > updatedMaxLeaseDurationForRenewals
      ) {
        return {
          isValid: false,
          propertySid: update.propertySid,
          humanReadableError: `Min lease duration for renewals (${updatedMinLeaseDurationForRenewals}) cannot be greater than max lease duration for renewals (${updatedMaxLeaseDurationForRenewals})`,
        }
      }

      const effectiveMinLeaseDurationForRenewals =
        updatedMinLeaseDurationForRenewals ?? updatedMinLeaseDuration
      const effectiveMaxLeaseDurationForRenewals =
        updatedMaxLeaseDurationForRenewals ?? updatedMaxLeaseDuration
      if (effectiveMinLeaseDurationForRenewals > effectiveMaxLeaseDurationForRenewals) {
        return {
          isValid: false,
          propertySid: update.propertySid,
          humanReadableError: `Effective min lease duration for renewals (${effectiveMinLeaseDurationForRenewals}) cannot be greater than effective max lease duration for renewals (${effectiveMaxLeaseDurationForRenewals})`,
        }
      }
    }

    const isAnyBestTermFieldInUpdate =
      update.maxBestTerm !== undefined ||
      update.minBestTermForNewLeases !== undefined ||
      update.minBestTermForRenewals !== undefined
    if (isAnyBestTermFieldInUpdate || isAnyLeaseDurationFieldInUpdate) {
      const updatedMaxBestTerm = resolveNullableUpdate(update.maxBestTerm, property.maxBestTerm)
      const minBestTermUpdates = [
        {
          value: resolveNullableUpdate(
            update.minBestTermForNewLeases,
            property.minBestTermForNewLeases
          ),
          label: 'Minimum best term for new leases',
          maxLeaseDuration: updatedMaxLeaseDuration,
        },
        {
          value: resolveNullableUpdate(
            update.minBestTermForRenewals,
            property.minBestTermForRenewals
          ),
          label: 'Minimum best term for renewals',
          maxLeaseDuration: updatedMaxLeaseDurationForRenewals ?? updatedMaxLeaseDuration,
        },
      ]

      for (const { value, label, maxLeaseDuration } of minBestTermUpdates) {
        if (!isNotNullOrUndefined(value)) {
          continue
        }
        if (value < 1) {
          return {
            isValid: false,
            propertySid: update.propertySid,
            humanReadableError: `${label} must be at least 1`,
          }
        }
        if (isNotNullOrUndefined(updatedMaxBestTerm) && value > updatedMaxBestTerm) {
          return {
            isValid: false,
            propertySid: update.propertySid,
            humanReadableError: `${label} (${value}) cannot be greater than maximum best term (${updatedMaxBestTerm})`,
          }
        }
        if (value > maxLeaseDuration) {
          return {
            isValid: false,
            propertySid: update.propertySid,
            humanReadableError: `${label} (${value}) cannot be greater than the maximum lease duration (${maxLeaseDuration})`,
          }
        }
      }
    }

    if (
      isNotNullOrUndefined(update.renewalRateTargetLowerBound) ||
      isNotNullOrUndefined(update.renewalRateTargetUpperBound)
    ) {
      const renewalRateTargetLowerBound =
        update.renewalRateTargetLowerBound ?? property.renewalRateTargetLowerBound
      const renewalRateTargetUpperBound =
        update.renewalRateTargetUpperBound ?? property.renewalRateTargetUpperBound
      if (renewalRateTargetLowerBound > renewalRateTargetUpperBound) {
        return {
          isValid: false,
          propertySid: update.propertySid,
          humanReadableError: `Minimum target renewal rate (${renewalRateTargetLowerBound * 100}%) cannot be greater than maximum target renewal rate (${renewalRateTargetUpperBound * 100}%)`,
        }
      }
    }

    const staleUnitRentAdjustmentError = getStaleUnitRentAdjustmentUpdateError({
      update,
      storedProperty: property,
    })
    if (staleUnitRentAdjustmentError !== null) {
      return {
        isValid: false,
        propertySid: update.propertySid,
        humanReadableError: staleUnitRentAdjustmentError,
      }
    }
  }

  return {
    isValid: true,
  }
}

export enum PropertySyncMode {
  disabled = 'disabled',
  onboarding = 'onboarding',
  preparePricing = 'preparePricing',
  live = 'live',
}

export const propertySyncModeToDetails: Record<
  PropertySyncMode,
  {
    label: string
    getDescription: (pmsName: string) => string
  }
> = {
  [PropertySyncMode.disabled]: {
    label: 'Disabled',
    getDescription: (pmsName: string) =>
      `Property will not be synced with ${pmsName} and will not appear in Rentana.`,
  },
  [PropertySyncMode.onboarding]: {
    label: 'Onboarding',
    getDescription: (pmsName: string) =>
      `Sync pulls property data and pricing from ${pmsName}. Prices are not editable in Rentana.`,
  },
  [PropertySyncMode.preparePricing]: {
    label: 'Prepare pricing',
    getDescription: (pmsName: string) =>
      `Sync pulls property data from ${pmsName}, but prices are not synced in either direction. Used to prepare the property’s pricing (e.g. aligning base rents) before going live.`,
  },
  [PropertySyncMode.live]: {
    label: 'Live',
    getDescription: (pmsName: string) =>
      `Rentana writes prices to ${pmsName} during sync and when prices are changed in Rentana. Sync continues to pull property data.`,
  },
}

export function getLabelForPropertySyncMode(syncMode: PropertySyncMode): string {
  return propertySyncModeToDetails[syncMode].label
}

export type PropertyForSyncMode = {
  disabled: boolean
  pricingDisabled: boolean
  fetchPmsPricingDisabled: boolean
}

/**
 * Returns the sync mode of a property, based on the `disabled`, `pricingDisabled`, and
 * `fetchPmsPricingDisabled` fields. See the Property model in schema.prisma for more details.
 */
export function getPropertySyncMode<Property extends PropertyForSyncMode>(
  property: Property
): PropertySyncMode {
  if (property.disabled) {
    return PropertySyncMode.disabled
  }
  if (!property.pricingDisabled) {
    return PropertySyncMode.live
  }
  if (property.fetchPmsPricingDisabled) {
    return PropertySyncMode.preparePricing
  }
  return PropertySyncMode.onboarding
}

/**
 * Returns the field values for a property that are relevant for a given sync mode.
 */
export function getPropertyFieldValuesForSyncMode(syncMode: PropertySyncMode): {
  disabled: boolean
  pricingDisabled: boolean
  fetchPmsPricingDisabled: boolean
} {
  switch (syncMode) {
    case PropertySyncMode.disabled:
      return { disabled: true, pricingDisabled: true, fetchPmsPricingDisabled: true }
    case PropertySyncMode.onboarding:
      return { disabled: false, pricingDisabled: true, fetchPmsPricingDisabled: false }
    case PropertySyncMode.preparePricing:
      return { disabled: false, pricingDisabled: true, fetchPmsPricingDisabled: true }
    case PropertySyncMode.live:
      return { disabled: false, pricingDisabled: false, fetchPmsPricingDisabled: true }
    default:
      throw exhaustiveSwitchError(syncMode)
  }
}

export type PropertyForIsDemoProperty = {
  integrationInstance: {
    integrationType: Integration
  } | null
}

/**
 * Returns whether a property is a demo property based on its integrationInstance type. This checks
 * if the property's integrationInstance has integrationType === 'demo'.
 */
export function isDemoProperty<Property extends PropertyForIsDemoProperty>(
  property: Property
): boolean {
  return property.integrationInstance?.integrationType === 'demo'
}

export enum PropertyPmsMarketRentSyncFrequency {
  daily = 'daily',
  monthly = 'monthly',
  quarterly = 'quarterly',
}

export const PropertyPmsMarketRentSyncFrequencyToDetails: Record<
  PropertyPmsMarketRentSyncFrequency,
  {
    label: string
    description: string
  }
> = {
  [PropertyPmsMarketRentSyncFrequency.daily]: {
    label: 'Daily',
    description: `Syncs every day. If the sync value is set to “Current,” only syncs if the market rent has changed.`,
  },
  [PropertyPmsMarketRentSyncFrequency.monthly]: {
    label: 'Monthly',
    description: `Syncs on the 1st of the month`,
  },
  [PropertyPmsMarketRentSyncFrequency.quarterly]: {
    label: 'Quarterly',
    description: `Syncs on the 1st of the quarter`,
  },
}

export function getLabelForPropertyPmsMarketRentSyncFrequency(
  syncFrequency: PropertyPmsMarketRentSyncFrequency
): string {
  return PropertyPmsMarketRentSyncFrequencyToDetails[syncFrequency].label
}

export enum PropertyPmsMarketRentSyncWindow {
  current = 'current',
  thirtyDays = 'thirtyDays',
  sixtyDays = 'sixtyDays',
  ninetyDays = 'ninetyDays',
}

export const PropertyPmsMarketRentSyncWindowToDetails: Record<
  PropertyPmsMarketRentSyncWindow,
  {
    label: string
    description?: string
  }
> = {
  [PropertyPmsMarketRentSyncWindow.current]: {
    label: 'Current',
  },
  [PropertyPmsMarketRentSyncWindow.thirtyDays]: {
    label: '30d average',
  },
  [PropertyPmsMarketRentSyncWindow.sixtyDays]: {
    label: '60d average',
  },
  [PropertyPmsMarketRentSyncWindow.ninetyDays]: {
    label: '90d average',
  },
}

export function getLabelForPropertyPmsMarketRentSyncWindow(
  syncWindow: PropertyPmsMarketRentSyncWindow
): string {
  return PropertyPmsMarketRentSyncWindowToDetails[syncWindow].label
}
